'use client';

import type { ReportQuestion } from '@dte/otw-azure-functions/src/report/questions/ReportQuestion';
import type { PropsWithChildren, ReactNode } from 'react';
import { useReportAnswerValueSelected } from '../_atoms/draftReport/stepData';
import { useCurrentStep } from '../_atoms/draftReport/useCurrentStep';

interface PropsWithCategories extends PropsWithChildren {
	categories?: string[];
}

function HideFromCategories(_props: PropsWithCategories): ReactNode {
	// TODO: work on this
	return null;
}

interface PropsWithSteps extends PropsWithChildren {
	steps?: string[];
}

export function HideFromSteps(props: PropsWithSteps): ReactNode {
	// This component is used to hide the ObservationButton from certain steps
	const currentStep = useCurrentStep();

	if (props.steps?.includes(currentStep)) {
		return null;
	}

	return props.children;
}

interface ShowIfAnswerSelectedProps extends PropsWithChildren {
	question: ReportQuestion;

	// One or more answer keys to check for selection. If at least one is selected, the content will be shown
	answerKey?: string;

	// Alternate content to show if not selected
	contentIfUnselected?: ReactNode;

	// If true, the content will always be shown even when the selected value is undefined
	showIfSelectedIsUndefined?: boolean;
}

export function ShowIfAnswerSelected(props: ShowIfAnswerSelectedProps): ReactNode {
	const { question, answerKey } = props;

	// Check if the current question and answer are selected
	const isSelected = useReportAnswerValueSelected(question, answerKey);

	// If the value is selected, show the content
	if (isSelected) {
		return props.children;
	}

	// Content has either been intentionally unselected or we want to always show it any time selected isn't true
	if (isSelected === false || props.showIfSelectedIsUndefined) {
		return props.contentIfUnselected ?? null;
	}

	return null;
}

interface ShowIfAnswerUnselectedProps extends PropsWithChildren {
	question: ReportQuestion;

	// One or more answer keys to check for selection. If at least one is selected, the content will be shown
	answerKey?: string;

	// If true, the content will always be shown even when the selected value is undefined
	showIfSelectedIsUndefined?: boolean;
}

export function ShowIfAnswerUnselected(props: ShowIfAnswerUnselectedProps): ReactNode {
	const { question, answerKey } = props;

	// Check if the current question and answer are selected
	const isSelected = useReportAnswerValueSelected(question, answerKey);

	// Content has either been intentionally unselected or we want to always show it any time selected isn't true
	if (isSelected === false || props.showIfSelectedIsUndefined) {
		return props.children;
	}

	return null;
}
